/**
 * DocumentTemplatesScreen — manage reusable document checklists.
 *
 * Each template is `{ name, documents[] }`. The accountant picks one when
 * creating a new campaign and it pre-fills the document list, saving them
 * from re-typing "טופס 102 / תלוש שכר / מע״מ" every month.
 */

import {
  Badge,
  Button,
  Card,
  Chip,
  Divider,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useEffect, useState } from "react";

import { api } from "../lib/api";

interface Template {
  template_id: string;
  name: string;
  documents: string[];
  created_at: string;
  updated_at: string;
}

export function DocumentTemplatesScreen() {
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Template | "new" | null>(null);

  const refresh = async () => {
    try {
      const r = await api.templatesList();
      setTemplates(r.templates);
    } catch {
      setError("טעינת התבניות נכשלה.");
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  if (error) return <Text c="red">{error}</Text>;
  if (templates === null)
    return (
      <Group py="lg">
        <Loader size="sm" />
      </Group>
    );

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>תבניות מסמכים</Title>
        <Button onClick={() => setEditing("new")}>תבנית חדשה</Button>
      </Group>
      <Text c="dimmed" size="sm">
        תבנית = רשימה שמורה של מסמכים. בחר תבנית כשתיצור קמפיין חדש כדי
        לחסוך הקלדה חוזרת של אותם תוויות.
      </Text>

      {templates.length === 0 ? (
        <Card withBorder padding="xl">
          <Text c="dimmed" ta="center">
            עדיין אין תבניות. לחץ "תבנית חדשה" כדי להתחיל.
          </Text>
        </Card>
      ) : (
        <Stack gap="md">
          {templates.map((t) => (
            <Card key={t.template_id} withBorder padding="lg" dir="rtl">
              <Group justify="space-between" align="flex-start">
                <Stack gap={8} style={{ flex: 1 }}>
                  <Title order={4}>{t.name}</Title>
                  <Group gap="xs" wrap="wrap">
                    {t.documents.map((d) => (
                      <Badge key={d} variant="light" color="blue" size="md" radius="sm">
                        {d}
                      </Badge>
                    ))}
                  </Group>
                </Stack>
                <Group gap={4} wrap="nowrap">
                  <Button size="xs" variant="subtle" color="gray" onClick={() => setEditing(t)}>
                    ערוך
                  </Button>
                  <Divider orientation="vertical" />
                  <Button
                    size="xs"
                    variant="subtle"
                    color="red"
                    onClick={async () => {
                      if (!confirm(`למחוק את "${t.name}"?`)) return;
                      await api.templateDelete(t.template_id);
                      await refresh();
                    }}
                  >
                    מחק
                  </Button>
                </Group>
              </Group>
            </Card>
          ))}
        </Stack>
      )}

      <TemplateEditor
        template={editing}
        onClose={() => setEditing(null)}
        onSaved={async () => {
          setEditing(null);
          await refresh();
        }}
      />
    </Stack>
  );
}

function TemplateEditor({
  template,
  onClose,
  onSaved,
}: {
  template: Template | "new" | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [docs, setDocs] = useState<string[]>([]);
  const [draftDoc, setDraftDoc] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (template === null) {
      return;
    }
    if (template === "new") {
      setName("");
      setDocs([]);
    } else {
      setName(template.name);
      setDocs(template.documents);
    }
    setDraftDoc("");
  }, [template]);

  if (template === null) return null;
  const isNew = template === "new";

  const addDraft = () => {
    const d = draftDoc.trim();
    if (!d) return;
    setDocs([...docs, d]);
    setDraftDoc("");
  };

  const save = async () => {
    if (!name.trim() || docs.length === 0) return;
    setBusy(true);
    try {
      if (isNew) {
        await api.templateCreate(name.trim(), docs);
      } else {
        await api.templateUpdate(template.template_id, {
          name: name.trim(),
          documents: docs,
        });
      }
      await onSaved();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened onClose={onClose} title={isNew ? "תבנית חדשה" : "עריכת תבנית"}>
      <Stack>
        <TextInput
          label="שם התבנית"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          required
          dir="rtl"
        />
        <Group align="end">
          <TextInput
            label="הוסף מסמך"
            value={draftDoc}
            onChange={(e) => setDraftDoc(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addDraft();
              }
            }}
            style={{ flex: 1 }}
            dir="rtl"
          />
          <Button onClick={addDraft} variant="light" disabled={!draftDoc.trim()}>
            הוסף
          </Button>
        </Group>
        {docs.length > 0 && (
          <Group gap="xs">
            {docs.map((d, i) => (
              <Chip
                key={i}
                checked
                onClick={() => setDocs(docs.filter((_, j) => j !== i))}
              >
                {d} ✕
              </Chip>
            ))}
          </Group>
        )}
        <Text size="xs" c="dimmed">
          לחץ על תווית כדי להסיר אותה.
        </Text>
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            ביטול
          </Button>
          <Button
            onClick={save}
            loading={busy}
            disabled={!name.trim() || docs.length === 0}
          >
            שמור
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
